import { Link, useParams } from 'react-router-dom';
import { AsyncBoundary, Card } from '@kollez/design-system';
import { useSkillTaxonomyDetail } from '@/lib/queries';

/**
 * /skills-catalog/:slug — one skill's detail.
 *
 * Consumes /skill-taxonomy/:slug which enriches the doc with demand
 * (opportunity counts where the skill is required/preferred) and the
 * total live badges issued for it. Level ladder is displayed rank-ordered
 * so a candidate can see the progression.
 */

const TAX_LABEL: Record<string, string> = {
  skill: 'Technical skill',
  psychocognitive: 'Psychocognitive',
  realwork: 'Real-work',
  language: 'Language',
  soft_skill: 'Soft skill',
  domain: 'Domain',
};

export function SkillDetailPage() {
  const { slug = '' } = useParams();
  const q = useSkillTaxonomyDetail(slug);
  const s = q.data ?? null;

  return (
    <AsyncBoundary
      kind="detail"
      loading={q.isLoading}
      error={q.error}
      empty={!q.isLoading && !s}
      emptyTitle="Skill not found"
      emptyHint="This slug isn't in the canonical taxonomy. It may have been renamed or deprecated."
      onRetry={() => void q.refetch()}
    >
      {s && (
        <div className="stack">
          <div className="page-header">
            <div className="ph-text">
              <p className="eyebrow">
                <Link to="/skills-catalog">Skills catalog</Link> · {TAX_LABEL[s.taxonomy] ?? s.taxonomy}
              </p>
              <h1 className="h-page">{s.displayName}</h1>
              <p className="subhead">
                <code>{s.slug}</code>
                {s.status && s.status !== 'live' && (
                  <> · <span className={`badge ${s.status === 'deprecated' ? 'badge-danger' : 'badge-warning'}`}>{s.status}</span></>
                )}
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <Card padding="md">
              <div className="cell-sub">Required by</div>
              <div className="h-section">{s.demand.required}</div>
              <div className="cell-sub">active opportunities</div>
            </Card>
            <Card padding="md">
              <div className="cell-sub">Preferred by</div>
              <div className="h-section">{s.demand.preferred}</div>
              <div className="cell-sub">active opportunities</div>
            </Card>
            <Card padding="md">
              <div className="cell-sub">Live badges</div>
              <div className="h-section">{s.badgesIssued}</div>
              <div className="cell-sub">students hold this credential</div>
            </Card>
          </div>

          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px] items-start">
            <div className="stack">
              {s.levelLadder && s.levelLadder.length > 0 ? (
                <Card padding="md">
                  <h2 className="h-section">Level ladder</h2>
                  <ol className="stack">
                    {s.levelLadder.slice().sort((a, b) => a.rank - b.rank).map((l) => (
                      <li key={l.rank} className="p-3 border border-line-tertiary rounded flex items-center gap-3">
                        <span className="badge">#{l.rank}</span>
                        <span className="font-medium">{l.level}</span>
                      </li>
                    ))}
                  </ol>
                </Card>
              ) : (
                <Card padding="md">
                  <h2 className="h-section">Level ladder</h2>
                  <p className="cell-sub m-0">
                    This skill doesn't declare an explicit ladder. Badges are single-tier.
                  </p>
                </Card>
              )}
            </div>

            <aside className="stack">
              {s.aliases.length > 0 && (
                <Card padding="md">
                  <h2 className="h-section">Aliases</h2>
                  <div className="cell-sub mb-2">
                    Free-form spellings students may enter — canonicalized to <code>{s.slug}</code>.
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {s.aliases.map((a) => (
                      <span key={a} className="badge">{a}</span>
                    ))}
                  </div>
                </Card>
              )}

              {typeof s.defaultExpiryDays === 'number' && (
                <Card padding="md">
                  <h2 className="h-section">Credential expiry</h2>
                  <p className="m-0">
                    Badges for this skill are valid for <b>{s.defaultExpiryDays} days</b> from issue.
                    Students need to re-assess to renew.
                  </p>
                </Card>
              )}
            </aside>
          </div>
        </div>
      )}
    </AsyncBoundary>
  );
}
